/* The ORGANIZER WEBSITE (Phase 6, Level 1) — /{org-slug}.
   A permanent branded web presence with the controlled mandatory structure:
   Header · Hero · Auctions (auto-populated) · optional modules ·
   How can I Bid? · Footer. The organizer customizes, never deletes. */
import { SearchX } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { HowToBid } from "@/components/website/HowToBid";
import { WebsiteAuctions } from "@/components/website/WebsiteAuctions";
import { WebsiteFooter } from "@/components/website/WebsiteFooter";
import { WebsiteHeader } from "@/components/website/WebsiteHeader";
import { WebsiteHero } from "@/components/website/WebsiteHero";
import { WebsiteModules } from "@/components/website/WebsiteModules";
import { api, mediaUrlFromMap } from "@/lib/api";
import { ensurePairingLoaded } from "@/lib/fonts";
import { useHostTenant, useOrgSlug } from "@/lib/hostTenant";
import { localized } from "@/lib/intl";
import { getPublicBase, useJsonLd, usePageMeta } from "@/lib/seo";
import { brandStyle, radiusAttr } from "@/lib/theme";

export default function OrganizerWebsitePage() {
  const orgSlug = useOrgSlug();
  const tenant = useHostTenant();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const [data, setData] = useState();

  useEffect(() => {
    setData(undefined);
    api
      .get(`/public/organizers/${orgSlug}`)
      .then(({ data: payload }) => setData(payload))
      .catch(() => setData(null));
  }, [orgSlug]);

  useEffect(() => {
    if (data?.organizer?.theme?.font_pairing) ensurePairingLoaded(data.organizer.theme.font_pairing);
  }, [data?.organizer?.theme?.font_pairing]);

  const orgLogo = data?.organizer ? mediaUrlFromMap(data.media, data.organizer.theme?.logo_media_id) : null;
  usePageMeta({
    title: data?.organizer ? `${data.organizer.name} — ${t("web.auctions")}` : undefined,
    description: data?.organizer ? localized(data.organizer.description, locale) : undefined,
    // WS9: imagen social del organizador = su logo; si no hay, cae a la de marca.
    image: orgLogo || undefined,
    imageAlt: data?.organizer?.name,
    // WS6: /:orgSlug captura cualquier slug de primer nivel — si el organizador
    // no existe, este estado equivale a un 404 y NUNCA debe indexarse.
    robots: data === null ? "noindex, nofollow" : undefined,
  });
  useJsonLd(
    data?.organizer
      ? {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: data.organizer.name,
          // WS15: URL canónica sobre PUBLIC_BASE (sin origin del preview).
          url: tenant ? getPublicBase() : `${getPublicBase()}/${orgSlug}`,
          logo: orgLogo || undefined,
          description: localized(data.organizer.description, locale) || undefined,
        }
      : null
  );

  if (data === undefined) {
    return (
      <div className="flex flex-col gap-6" data-testid="website-skeleton">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-[420px] w-full" />
        <div className="max-w-6xl mx-auto w-full px-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-64 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24">
        <EmptyState icon={SearchX} testid="website-not-found" title={t("brand.notFoundTitle")} description={t("brand.notFoundDesc")} />
      </div>
    );
  }

  const { organizer, website } = data;

  return (
    <div
      className="brand-scope bg-white"
      data-radius={radiusAttr(organizer.theme)}
      style={brandStyle(organizer.theme)}
      data-testid="organizer-website"
    >
      <WebsiteHeader organizer={organizer} website={website} media={data.media} />
      <WebsiteHero organizer={organizer} website={website} media={data.media} />
      <WebsiteAuctions organizer={organizer} website={website} events={data.events} media={data.media} />
      <WebsiteModules data={data} />
      <HowToBid website={website} />
      <WebsiteFooter organizer={organizer} website={website} media={data.media} />
    </div>
  );
}
